import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Pencil, Trash2 } from "lucide-react";
import { MedicationDetails } from "./MedicationDetails";
import { MedicationEditForm } from "./MedicationEditForm";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface Medication {
  id: string;
  name: string;
  active: boolean;
  dosage: string;
  frequency: string;
  expiry_date: string | null;
  start_date: string;
  end_date: string | null;
  notes: string | null;
}

interface MedicationListProps {
  medications: Medication[];
  getExpiryStatus: (date: string | null) => string | null;
  getStatusColor: (status: string | null) => string;
}

export const MedicationList = ({
  medications,
  getExpiryStatus,
  getStatusColor,
}: MedicationListProps) => {
  const [selectedMedication, setSelectedMedication] = useState<Medication | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleViewDetails = (medication: Medication) => {
    setSelectedMedication(medication);
    setIsDetailsOpen(true);
  };

  const handleEdit = (medication: Medication) => {
    setSelectedMedication(medication);
    setIsEditOpen(true);
  };

  const handleDelete = async (medicationId: string) => {
    try {
      const { error } = await supabase
        .from("medications")
        .delete()
        .eq("id", medicationId);

      if (error) throw error;

      toast({
        title: "Médicament supprimé",
        description: "Le médicament a été supprimé avec succès",
      });
      
      queryClient.invalidateQueries({ queryKey: ["medications"] });
    } catch (error) {
      console.error("Error deleting medication:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le médicament",
      });
    }
  };

  return (
    <>
      <div className="space-y-4">
        {medications.map((medication) => (
          <div
            key={medication.id}
            className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="rounded-full w-10 h-10 bg-primary/10 flex items-center justify-center">
                {medication.active ? (
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                ) : (
                  <XCircle className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <h4 className="font-semibold">{medication.name}</h4>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary">{medication.dosage}</Badge>
                  <Badge variant="outline">{medication.frequency}</Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {medication.expiry_date && (
                <div className={`text-sm px-2 py-1 rounded-full ${
                  getStatusColor(getExpiryStatus(medication.expiry_date))
                }`}>
                  {new Date(medication.expiry_date).toLocaleDateString()}
                </div>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => handleViewDetails(medication)}
              >
                Détails
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(medication)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(medication.id)}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <MedicationDetails
        medication={selectedMedication}
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
      />

      <MedicationEditForm
        medication={selectedMedication}
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
      />
    </>
  );
};